// index.tsx
// PatientName component for displaying a patient's name with a tooltip and alert if Athena data is missing.
// Used in tables and detail views to provide context and warnings about patient data source.

import { styled } from "@stitches/react";
import TextWithTooltip from "@/components/TextWithTooltip";
import { Tooltip } from "@/vendor/Forge/dist";
import AlertIcon from "../Icons/alert";

/**
 * Wrapper for the patient name and alert icon.
 */
const Wrapper = styled("div", {
  display: "flex",
  gap: 10,
  alignItems: "center",
});

/**
 * Props for the PatientName component
 * @property name - The patient's name to display
 * @property isMissingAthenaData - Whether Athena data is missing for this patient
 */
interface Props {
  name: string;
  isMissingAthenaData: boolean;
}

/**
 * PatientName Component
 * Displays a patient's name, and if Athena data is missing, shows an alert icon with a tooltip.
 * @param name - The patient's name
 * @param isMissingAthenaData - Whether Athena data is missing
 */
const PatientName = ({ name, isMissingAthenaData }: Props) => {
  return (
    <Wrapper data-testid="patient-name-wrapper">
      {isMissingAthenaData && (
        <Tooltip
          id={`tool-missing-${name}`}
          text="Patient does not exist in Athena. In order to process authorization, please create a record."
        >
          <AlertIcon />
        </Tooltip>
      )}
      <TextWithTooltip id="patient-icon" text={name} textTooltip={name} />
    </Wrapper>
  );
};

export default PatientName;
